import type { CSSProperties } from 'react';
import type { KnownIssueStatus } from '../api/useKnownIssues.ts';
import type { ProgramStatus } from '../api/usePrograms.ts';
import type { SubmissionStatus, SubmissionType } from '../api/useSubmissions.ts';
import { Ikon } from './Ikon.tsx';
import { hypoteseOppforing, kjentFeilOppforing, programOppforing, statusEtikett, statusOppforing, tokenVar } from './status.ts';

/**
 * Hypotesens pille (H2.3): ÉTT ord med ikon (preferanse 2026-09-19). Ordet er leietakerens
 * (`HypothesisStatusWording.label`) og kommer inn som `navn`; ikonet, fargen og formen er
 * plattformens, fra `hypothesisStatus` i theme.ts. Utkast er stiplet uten flate.
 */
export function HypotesePille({ noekkel, navn, liten }: { noekkel: string; navn: string; liten?: boolean }) {
  const o = hypoteseOppforing(noekkel);
  const style = { '--pille-fg': tokenVar(o.fg), '--pille-bg': tokenVar(o.bg) } as CSSProperties;
  return (
    <span className={`panel-pille panel-pille-${o.variant}${liten ? ' panel-pille-liten' : ''}`} style={style} data-status={noekkel}>
      <Ikon navn={o.ikon} />
      {navn}
    </span>
  );
}

/** Programmets status (M3.1): utkast er stiplet, avbrutt er den eneste fylte — formen skiller dem. */
export function ProgramPille({ status, liten }: { status: ProgramStatus; liten?: boolean }) {
  const o = programOppforing(status);
  const style = { '--pille-fg': tokenVar(o.fg), '--pille-bg': tokenVar(o.bg) } as CSSProperties;
  return (
    <span className={`panel-pille panel-pille-${o.variant}${liten ? ' panel-pille-liten' : ''}`} style={style} data-status={status}>
      <Ikon navn={o.ikon} />
      {o.etikett}
    </span>
  );
}

/** Kjent feil-pillen (M2.4): «Aktiv» eller «Rettet», stiplet når rettet (DD-30). Samme regel: farge OG ikon OG tekst. */
export function KjentFeilPille({ status, liten }: { status: KnownIssueStatus; liten?: boolean }) {
  const o = kjentFeilOppforing(status);
  const style = { '--pille-fg': tokenVar(o.fg), '--pille-bg': tokenVar(o.bg) } as CSSProperties;
  return (
    <span className={`panel-pille panel-pille-${o.variant}${liten ? ' panel-pille-liten' : ''}`} style={style} data-status={status}>
      <Ikon navn={o.ikon} />
      {o.etikett}
    </span>
  );
}

/**
 * Statuspille: farge OG ikon OG tekstetikett (DD-08, brand-ui-guidelines). Etiketten er
 * sporets (DD-22). `variant` skiller Planlagt (kontur) fra Retter nå (fylt) — aldri nyanse.
 */
export function Pille({ status, type, liten }: { status: SubmissionStatus; type: SubmissionType; liten?: boolean }) {
  const o = statusOppforing(status);
  const style = { '--pille-fg': tokenVar(o.fg), '--pille-bg': tokenVar(o.bg) } as CSSProperties;
  return (
    <span className={`panel-pille panel-pille-${o.variant}${liten ? ' panel-pille-liten' : ''}`} style={style} data-status={status}>
      <Ikon navn={o.ikon} />
      {statusEtikett(status, type)}
    </span>
  );
}
